import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  LogOut,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBusiness } from '@/contexts/BusinessContext';
import NotificationBell from '../ui/NotificationBell';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const { currentBusiness } = useBusiness();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header className="bg-gray-950 border-b border-gray-800 lg:fixed lg:top-0 lg:right-0 lg:left-64 lg:z-40">
      <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
        {/* Mobile menu button */}
        <button
          type="button"
          className="lg:hidden p-2 rounded-xl text-gray-400 hover:text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
          onClick={onMenuClick}
        >
          <Menu className="h-6 w-6" />
        </button>

        {/* Logo - visible on mobile */}
        <div className="lg:hidden flex items-center">
          {currentBusiness?.logo ? (
            <img
              src={currentBusiness.logo}
              alt={currentBusiness.name}
              className="w-8 h-8 rounded-lg object-cover mr-2"
            />
          ) : null}
          <span className="text-lg font-bold text-white truncate max-w-[40vw]">
            {currentBusiness?.name || 'Mi Negocio'}
          </span>
        </div>

        {/* Spacer for desktop */}
        <div className="hidden lg:block" />

        {/* Right side */}
        <div className="flex items-center space-x-3">
          {/* Notifications */}
          <NotificationBell />

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center space-x-3 p-2 rounded-xl text-gray-400 hover:text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
            >
              {currentUser?.profilePhotoUrl ? (
                <img
                  src={currentUser.profilePhotoUrl}
                  alt={`${currentUser.firstName} ${currentUser.lastName}`}
                  className="w-9 h-9 rounded-full object-cover ring-2 ring-gray-700"
                />
              ) : currentUser?.role === 'admin' && currentBusiness?.logo ? (
                <img
                  src={currentBusiness.logo}
                  alt={currentBusiness.name}
                  className="w-9 h-9 rounded-full object-cover ring-2 ring-amber-500/50"
                />
              ) : currentUser?.role === 'instructor' ? (
                <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center ring-2 ring-green-500/50">
                  <span className="text-white text-sm font-bold">
                    {currentUser?.firstName?.charAt(0) || 'I'}
                  </span>
                </div>
              ) : (
                <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center ring-2 ring-gray-700">
                  <span className="text-white text-sm font-bold">
                    {currentUser?.firstName?.charAt(0) || 'U'}
                  </span>
                </div>
              )}
              <span className="hidden md:block text-sm font-medium text-gray-300">
                {currentUser?.firstName}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </button>

            {/* User dropdown */}
            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-56 bg-gray-900 rounded-2xl shadow-xl py-2 z-50 border border-gray-800">
                  {/* User info header */}
                  <div className="px-4 py-3 border-b border-gray-800">
                    <p className="text-sm font-medium text-white">
                      {currentUser?.firstName} {currentUser?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {currentUser?.email}
                    </p>
                  </div>

                  <div className="py-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2.5 text-sm text-red-400 hover:bg-gray-800 hover:text-red-300 transition-colors"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Cerrar Sesión
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
